<div class="draft-root" [class.tape-hidden]="isTapeCollapsed()">
  @if (isLoading()) {
    <div class="dc-loading">Loading draft...</div>
  } @else if (error()) {
    <div class="dc-error">
      <p>{{ error() }}</p>
      <button (click)="goBack()">Back to Leagues</button>
    </div>
  } @else if (league()) {

    <header class="dc-header">
      <div>
        <div class="dc-header-eyebrow">{{ draftRoundInfo() }}</div>
        <h1 class="dc-header-title">{{ league()!.name }}</h1>
      </div>
      <div class="dc-header-actions">
        <div class="dc-connection-dot" [class.dc-connection-dot--on]="socketService.isConnected()"
             [title]="socketService.isConnected() ? 'Connected' : 'Connecting...'"></div>
        <button class="db-btn-ghost" (click)="toggleCheatSheet()" [class.dc-btn-active]="isCheatSheetOpen()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
          </svg>
          Player list
        </button>
        <button class="db-btn-ghost" (click)="toggleTape()" [class.dc-btn-active]="!isTapeCollapsed()" title="Toggle The Tape">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
            <circle cx="8" cy="14" r="3"></circle>
            <circle cx="16" cy="14" r="3"></circle>
            <path d="M11 14 h2"></path>
            <path d="M5 8 h14"></path>
          </svg>
          The Tape
        </button>
        <button class="db-btn-ghost dc-chat-btn" (click)="toggleChatDrawer()" [class.dc-btn-active]="isChatDrawerOpen()">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          Chat
          @if (unreadChatCount() > 0 && !isChatDrawerOpen()) {
            <span class="dc-chat-badge">{{ unreadChatCount() }}</span>
          }
        </button>
      </div>
    </header>

    <div class="dc-grid">

      <!-- LEFT: draft stage -->
      <div class="col-left">
        <app-draft-stage
          [leagueId]="league()!.id!"
          [isCommissioner]="isCommissioner()"
          [teamBudget]="league()!.team_budget"
          [maxBid]="userBudget()?.maxBid ?? null">
        </app-draft-stage>
      </div>

      <!-- MID: watchlist + your hand -->
      <div class="col-mid">

        <!-- Watchlist -->
        <div class="dc-panel dc-panel--watchlist">
          <div class="dc-panel-header">
            <span class="dc-serif">Your watchlist</span>
            <span class="dc-mono dc-mute dc-panel-hint">
              {{ watchlistPlayers().length }} CARDS · TAP TO NOMINATE
            </span>
          </div>
          <div class="dc-cards-2col">
            @for (player of watchlistPlayers(); track player.id) {
              <app-player-card
                [player]="watchlistCardData(player)"
                [clickable]="canNominate"
                (cardClick)="onWatchlistPlayerClick(player)">
              </app-player-card>
            }
            @empty {
              <div class="dc-watchlist-empty">
                Open the player list and star players to build your watchlist
              </div>
            }
          </div>
        </div>

        <!-- Your hand -->
        <div class="dc-panel dc-panel--hand">
          <div class="dc-hand-header">
            <span class="dc-mono dc-mute">YOUR HAND</span>
            @if (userBudget(); as budget) {
              <span class="dc-mono dc-mute">
                ${{ budget.remaining }} LEFT · {{ budget.playersDrafted }}/{{ rosterSize }}
              </span>
            }
          </div>
          <div class="dc-cards-5col">
            @for (entry of userTeamHistory(); track entry.draft_position) {
              <app-player-card [player]="handCardData(entry)" [nameAbbrev]="true" [showPhoto]="false"
                [clickable]="true" (cardClick)="onHandCardClick(entry)">
              </app-player-card>
            }
            @for (slot of emptySlots(userTeamHistory().length); track slot) {
              <div class="dc-card-slot">
                <span>+ SLOT</span>
              </div>
            }
          </div>
        </div>

      </div>

      <!-- RIGHT: opposing hands -->
      <div class="col-right">
        <div class="dc-opposing-label dc-mono dc-mute">OPPOSING HANDS</div>
        @for (team of otherTeams(); track team.team_id; let i = $index) {
          <div class="dc-team-mat" [style.--team-color]="teamColor(i)">
            <div class="dc-mat-header">
              <div class="dc-mat-name-group">
                <span class="dc-mat-name">{{ team.team_name }}</span>
                <div class="dc-connection-dot" [class.dc-connection-dot--on]="isUserOnline(team.user_id)"
                     [title]="isUserOnline(team.user_id) ? team.team_name + ' is online' : team.team_name + ' is offline'"></div>
              </div>
              <span class="dc-mono dc-mute dc-mat-stats">
                ${{ teamRemaining(team) }} · {{ teamFilled(team) }}/{{ rosterSize }}
              </span>
            </div>
            <div class="dc-mat-slots">
              @for (slot of allRosterSlots(); track slot) {
                <div class="dc-mat-slot"
                     [class.dc-mat-slot--filled]="slot < teamFilled(team)"
                     [style.--slot-color]="slotPositionColor(team, slot)"
                     [style.cursor]="slot < teamFilled(team) ? 'pointer' : ''"
                     (click)="slot < teamFilled(team) && onSlotClick(team, slot)"></div>
              }
            </div>
          </div>
        }
        @empty {
          <div class="dc-mute dc-mono" style="font-size:11px; letter-spacing:1px">
            NO OTHER TEAMS YET
          </div>
        }
      </div>

    </div>

    <!-- Cheat Sheet Drawer -->
    <div class="cheat-sheet-drawer" [class.open]="isCheatSheetOpen()">
      <div class="cheat-sheet-drawer-header">
        <h3>Player List</h3>
        <button class="close-btn" (click)="closeCheatSheet()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <app-cheat-sheet
        [leagueId]="league()!.id!"
        [isOpen]="isCheatSheetOpen()"
        [teamBudget]="league()!.team_budget"
        [watchlistPlayerIds]="watchlistPlayerIds()"
        (playerSelected)="onCheatSheetPlayerSelected($event)"
        (watchlistToggled)="toggleWatchlist($event)">
      </app-cheat-sheet>
    </div>

    <!-- Chat Drawer -->
    <div class="chat-drawer" [class.open]="isChatDrawerOpen()">
      <div class="chat-drawer-header">
        <h3>Chat</h3>
        <button class="close-btn" (click)="closeChatDrawer()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <app-chat [leagueId]="league()!.id!" [isOpen]="isChatDrawerOpen()" [systemMessages]="systemMessages()"></app-chat>
    </div>

    <!-- Hand Card Modal -->
    <app-modal [isOpen]="isHandCardModalOpen" (closed)="closeHandCardModal()" maxWidth="304px" [showHeader]="false" bodyPadding="5px">
      @if (selectedHandCard(); as card) {
        <div class="dc-card-modal-inner">
          <button type="button" class="dc-card-modal-close" (click)="closeHandCardModal()" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <div class="dc-card-modal-pos-badge" [attr.data-pos]="card.position.toLowerCase()">
            {{ card.position }}
          </div>
          <div class="dc-card-modal-portrait">
            @if (card.photo) {
              <img [src]="card.photo" [alt]="card.first_name + ' ' + card.last_name" />
            } @else {
              <svg width="80" height="80" viewBox="0 0 100 100" style="opacity:0.18">
                <circle cx="50" cy="34" r="18" fill="currentColor"/>
                <path d="M10 100 Q10 62 50 62 Q90 62 90 100 Z" fill="currentColor"/>
              </svg>
            }
          </div>
          <div class="dc-card-modal-footer">
            <div class="dc-card-modal-name dc-serif">
              {{ card.first_name }} {{ card.last_name }}
            </div>
            <div class="dc-card-modal-bottom">
              <span class="dc-card-modal-team">{{ card.pro_team }}</span>
              <span class="dc-card-modal-cost">{{ card.cost != null ? '$' + card.cost : '' }}</span>
            </div>
          </div>
        </div>
      }
    </app-modal>

    <!-- The Tape -->
    <app-the-tape [collapsed]="isTapeCollapsed()" [isCommissioner]="isCommissioner()" [leagueId]="league()!.id!"></app-the-tape>

    <!-- Countdown Overlay -->
    @if (socketService.countdownValue() !== null) {
      @for (val of [socketService.countdownValue()]; track val) {
        <div class="countdown-overlay">
          <span class="countdown-number">{{ val }}</span>
        </div>
      }
    }

  }
</div>
